import { find, get } from 'lodash-es';

import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFileSchemaWithId,
  FilesFileSchemaWithIdNullable,
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/files';

import type { SearchItemEntity } from '../redux/entities/normalized/search_item';

export function getOriginalFormat(
  formats: SearchItemEntity['formats'],
): FilesFormatSchemaWithIdNullable | undefined {
  return find(formats, { name: 'ORIGINAL' });
}

export function getOriginalFile(
  formats: SearchItemEntity['formats'],
  files: SearchItemEntity['files'],
): FilesFileSchemaWithIdNullable | undefined {
  const formatId = get(getOriginalFormat(formats), ['id']);
  const originalFile = find(files, { format_id: formatId });

  return originalFile;
}

/**
 * Get short display format or undefined.
 */
const MAX_FORMAT_LENGTH = 10;

export function shortDisplayFormat(
  format: string | null | undefined,
): string | undefined {
  return format && format.length <= MAX_FORMAT_LENGTH ? format : undefined;
}

/**
 * Resolves a human-readable container/codec label for a format, matching the
 * calculation used by the asset files panel (commercial name, then format, then
 * the format's internet media type). Returns undefined when none is available.
 */
export function getFormatLabel(
  format: FilesFormatSchemaWithIdNullable | undefined,
  mediaType?: string,
): string | undefined {
  const componentByMediaType = format?.components?.find(
    (item) => item.type?.toLowerCase() === mediaType,
  );
  const generalComponent = format?.components?.find(
    (item) => item.type === 'GENERAL',
  );
  const component =
    componentByMediaType ?? generalComponent ?? format?.components?.[0];
  const componentMetadata = component?.metadata;
  const formatMetadata = format?.metadata?.[0];

  return (
    componentMetadata?.commercial_name ??
    componentMetadata?.format ??
    formatMetadata?.format ??
    formatMetadata?.internet_media_type
  );
}

export function getFormatOrFileExtension(
  searchItem: SearchItemEntity | AssetsAssetSchemaTypedWithId,
  formats: FilesFormatSchemaWithId[] = [],
  files: FilesFileSchemaWithId[] = [],
): string {
  const shortFormat =
    'format' in searchItem ? shortDisplayFormat(searchItem.format) : undefined;
  if (shortFormat) {
    return shortFormat;
  }

  const formatsValue =
    'formats' in searchItem ? (searchItem.formats ?? []) : formats;
  const originalFile = getOriginalFile(
    formatsValue,
    'files' in searchItem ? searchItem.files : files,
  );
  const originalFileName = get(originalFile, ['original_name'], '');
  const match = originalFileName.match(/\.(\w+)$/);

  if (!match) {
    return '';
  }

  const [, extension] = match;

  return extension;
}

export const getAssetTypeFromExtension = (extension?: string) =>
  ['aep', 'prproj'].includes(extension ?? '') ? 'NLE_PROJECT' : 'ASSET';
